import { useState, useEffect, useCallback, useTransition } from 'react';

/**
 * useUIState - UI state management hook
 * Manages modals, sticky headers, scroll behavior, and navigation state
 */
const useUIState = (hasResults = false) => {
  // Modal states
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(() => {
    if (typeof window === 'undefined') return false;
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('openPricing') === 'true';
    } catch (e) { return false; }
  });
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showDomainGroupsModal, setShowDomainGroupsModal] = useState(false);
  const [showAnimationSettingsModal, setShowAnimationSettingsModal] = useState(false);
  const [showAnalysisHistoryModal, setShowAnalysisHistoryModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDataSourceInfo, setShowDataSourceInfo] = useState(false);

  // Sticky header and navigation states
  const [stickyHeaderVisible, setStickyHeaderVisible] = useState(false);
  const [showStickyNav, setShowStickyNav] = useState(false);
  const [activeSection, setActiveSection] = useState('prehled');
  
  // Additional UI states
  const [activePlaybook, setActivePlaybook] = useState(null);
  const [consentShown, setConsentShown] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 768 : false);
  const [historySelectedMetric, setHistorySelectedMetric] = useState('LCP');
  const [historyExpanded, setHistoryExpanded] = useState(false);
  
  // Mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Section navigation config
  const navSections = [
    { id: 'prehled', label: 'Přehled' },
    { id: 'vitals', label: 'Core Vitals' },
    { id: 'dalsi', label: 'Další' },
    { id: 'zarizeni', label: 'Zařízení' },
    { id: 'server', label: 'Server' },
    { id: 'historie', label: 'Historie' }
  ];

  // Scroll to section with smooth scroll
  const scrollToSection = useCallback((sectionId) => {
    setActiveSection(sectionId);
    
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      const headerOffset = 110; // sticky nav + main nav height
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  }, []);

  // Scroll listener for sticky header and sub-navigation
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      // Show sticky header when scrolled past 150px (hero area)
      setStickyHeaderVisible(scrollY > 150);
      // Show sticky sub-nav when results are present and scrolled past hero
      setShowStickyNav(hasResults && scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasResults]);

  // IntersectionObserver for active section tracking
  useEffect(() => {
    if (!hasResults) return;

    const options = {
      root: null,
      rootMargin: '-20% 0px -60% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const sectionId = entry.target.id.replace('section-', '');
          setActiveSection(sectionId);
        }
      });
    }, options);

    // Observe all sections
    navSections.forEach(section => {
      const element = document.getElementById(`section-${section.id}`);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [hasResults, navSections]);

  // Listen for global events to open modals
  useEffect(() => {
    const handlers = {
      openAdminPanel: () => setShowAdminPanel(true),
      openDomainGroups: () => setShowDomainGroupsModal(true),
      openAnimationSettings: () => setShowAnimationSettingsModal(true),
      openAnalysisHistory: () => setShowAnalysisHistoryModal(true),
      openPricing: () => setShowPricingModal(true),
      openAuth: () => setShowAuthModal(true)
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, []);

  // Close all modals
  const closeAllModals = useCallback(() => {
    setShowAuthModal(false);
    setShowPricingModal(false);
    setShowAdminPanel(false);
    setShowDomainGroupsModal(false);
    setShowAnimationSettingsModal(false);
    setShowAnalysisHistoryModal(false);
    setShowExportModal(false);
    setShowDataSourceInfo(false);
  }, []);

  // Toggle modal
  const toggleModal = useCallback((modalName, value) => {
    const setters = {
      auth: setShowAuthModal,
      pricing: setShowPricingModal,
      admin: setShowAdminPanel,
      domainGroups: setShowDomainGroupsModal,
      animationSettings: setShowAnimationSettingsModal,
      analysisHistory: setShowAnalysisHistoryModal,
      export: setShowExportModal,
      dataSourceInfo: setShowDataSourceInfo
    };

    const setter = setters[modalName];
    if (setter) {
      setter(typeof value === 'boolean' ? value : prev => !prev);
    }
  }, []);

  return {
    // Modal states
    showAuthModal,
    setShowAuthModal,
    showPricingModal,
    setShowPricingModal,
    showAdminPanel,
    setShowAdminPanel,
    showDomainGroupsModal,
    setShowDomainGroupsModal,
    showAnimationSettingsModal,
    setShowAnimationSettingsModal,
    showAnalysisHistoryModal,
    setShowAnalysisHistoryModal,
    showExportModal,
    setShowExportModal,
    showDataSourceInfo,
    setShowDataSourceInfo,

    // Sticky states
    stickyHeaderVisible,
    showStickyNav,
    activeSection,
    setActiveSection,

    // Additional UI states
    activePlaybook,
    setActivePlaybook,
    consentShown,
    setConsentShown,
    isMobile,
    historySelectedMetric,
    setHistorySelectedMetric,
    historyExpanded,
    setHistoryExpanded,

    // Navigation
    navSections,
    scrollToSection,

    // Actions
    closeAllModals,
    toggleModal
  };
};

export function usePageRouting() {
  const KNOWN_PATHS = ['/', '/audit', '/web-core-vitals-google-report-crux', '/admin', '/verify-email', '/reset-password', '/terms', '/privacy', '/dpa', '/lab-insights', '/lab-test', '/quick-tools', '/global-insights', '/credits', '/rankings', '/top-performers', '/czech-webs-ranking', '/health-check', '/pricing', '/checkout', '/checkout/success', '/checkout/cancel', '/quick-audit', '/history-monitoring', '/competitor-comparison', '/domain-comparison', '/methodology', '/monitoring', '/market-insights', '/homepage-archive', '/platform-status', '/ai-crawler-guide', '/blog', '/eshop-health-check'];
  
  const [currentPage, setCurrentPage] = useState(() => {
    const path = window.location.pathname;
    if (path === '/checkout/success') return 'checkout-success';
    if (path === '/checkout/cancel') return 'checkout-cancel';
    if (path === '/checkout') return 'checkout';
    if (path === '/pricing') return 'pricing';
    if (path === '/web-core-vitals-google-report-crux') return 'crux-alternativa';
    if (path === '/admin') return 'admin';
    if (path === '/audit') return 'audit';
    if (path === '/lab-insights') return 'lab-insights';
    if (path === '/global-insights') return 'global-insights';
    if (path === '/terms') return 'terms';
    if (path === '/privacy') return 'privacy';
    if (path === '/dpa') return 'dpa';
    if (path === '/credits') return 'credits';
    if (path === '/rankings') return 'rankings';
    if (path === '/top-performers') return 'top-performers';
    if (path === '/health-check') return 'health-check';
    if (path === '/quick-audit') return 'quick-audit';
    if (path === '/quick-tools') return 'quick-tools';
    if (path === '/lab-insights') return 'lab-insights';
    if (path === '/lab-test') return 'lab-test';
    if (path === '/history-monitoring') return 'history-monitoring';
    if (path === '/competitor-comparison') return 'competitor-comparison';
    if (path === '/domain-comparison') return 'domain-comparison';
    if (path === '/methodology') return 'methodology';
    if (path === '/czech-webs-ranking') return 'czech-webs-ranking';
    if (path === '/monitoring') return 'monitoring';
    if (path === '/market-insights') return 'market-insights';
    if (path === '/homepage-archive') return 'homepage-archive';
    if (path === '/platform-status') return 'platform-status';
    if (path === '/ai-crawler-guide') return 'ai-crawler-guide';
    if (path === '/blog') return 'blog';
    if (path.startsWith('/blog/')) return 'blog-article';
    if (path === '/eshop-health-check') return 'eshop-health-check';
    if (path.startsWith('/client-dashboard/')) return 'client-dashboard';
    if (!KNOWN_PATHS.includes(path) && !path.startsWith('/reset-password') && !path.startsWith('/client-dashboard/') && !path.startsWith('/blog/')) return '404';
    return 'home';
  });
  
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceInfo, setMaintenanceInfo] = useState({ message: null, estimatedTime: null });
  const [maintenanceChecked, setMaintenanceChecked] = useState(false);

  useEffect(() => {
    const checkMaintenance = async () => {
      try {
        const res = await fetch('/api/maintenance-status', { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setMaintenanceMode(data.maintenanceMode);
          setMaintenanceInfo({ message: data.message, estimatedTime: data.estimatedTime });
        }
      } catch (err) {
        console.warn('Failed to check maintenance status');
      }
      setMaintenanceChecked(true);
    };
    checkMaintenance();
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      if (path === '/web-core-vitals-google-report-crux') {
        setCurrentPage('crux-alternativa');
      } else if (path === '/admin') {
        setCurrentPage('admin');
      } else if (path === '/audit') {
        setCurrentPage('audit');
      } else if (path === '/lab-insights') {
        setCurrentPage('lab-insights');
      } else if (path === '/global-insights') {
        setCurrentPage('global-insights');
      } else if (path === '/terms') {
        setCurrentPage('terms');
      } else if (path === '/privacy') {
        setCurrentPage('privacy');
      } else if (path === '/dpa') {
        setCurrentPage('dpa');
      } else if (path === '/rankings') {
        setCurrentPage('rankings');
      } else if (path === '/top-performers') {
        setCurrentPage('top-performers');
      } else if (path === '/health-check') {
        setCurrentPage('health-check');
      } else if (path === '/checkout/success') {
        setCurrentPage('checkout-success');
      } else if (path === '/checkout/cancel') {
        setCurrentPage('checkout-cancel');
      } else if (path === '/checkout') {
        setCurrentPage('checkout');
      } else if (path === '/pricing') {
        setCurrentPage('pricing');
      } else if (path === '/quick-audit') {
        setCurrentPage('quick-audit');
      } else if (path === '/quick-tools') {
        setCurrentPage('quick-tools');
      } else if (path === '/lab-insights') {
        setCurrentPage('lab-insights');
      } else if (path === '/lab-test') {
        setCurrentPage('lab-test');
      } else if (path === '/history-monitoring') {
        setCurrentPage('history-monitoring');
      } else if (path === '/competitor-comparison') {
        setCurrentPage('competitor-comparison');
      } else if (path === '/domain-comparison') {
        setCurrentPage('domain-comparison');
      } else if (path === '/methodology') {
        setCurrentPage('methodology');
      } else if (path === '/monitoring') {
        setCurrentPage('monitoring');
      } else if (path === '/market-insights') {
        setCurrentPage('market-insights');
      } else if (path === '/homepage-archive') {
        setCurrentPage('homepage-archive');
      } else if (path === '/platform-status') {
        setCurrentPage('platform-status');
      } else if (path === '/ai-crawler-guide') {
        setCurrentPage('ai-crawler-guide');
      } else if (path === '/blog') {
        setCurrentPage('blog');
      } else if (path.startsWith('/blog/')) {
        setCurrentPage('blog-article');
      } else if (path === '/credits') {
        setCurrentPage('credits');
      } else if (path === '/czech-webs-ranking') {
        setCurrentPage('czech-webs-ranking');
      } else if (path === '/eshop-health-check') {
        setCurrentPage('eshop-health-check');
      } else if (!KNOWN_PATHS.includes(path) && !path.startsWith('/reset-password') && !path.startsWith('/blog/')) {
        setCurrentPage('404');
      } else {
        setCurrentPage('home');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    const handleCustomNavigate = (e) => {
      if (e.detail && e.detail.page) {
        const page = e.detail.page;
        const pathMap = {
          'home': '/',
          'pricing': '/pricing',
          'quick-audit': '/quick-audit',
          'quick-tools': '/quick-tools',
          'lab-insights': '/lab-insights',
          'lab-test': '/lab-test',
          'history-monitoring': '/history-monitoring',
          'competitor-comparison': '/competitor-comparison',
          'domain-comparison': '/domain-comparison',
          'methodology': '/methodology',
          'monitoring': '/monitoring',
          'health-check': '/health-check',
          'platform-status': '/platform-status',
          'ai-crawler-guide': '/ai-crawler-guide',
          'blog': '/blog',
          'eshop-health-check': '/eshop-health-check'
        };
        const targetPath = pathMap[page] || '/';
        window.history.pushState({}, '', targetPath);
        setCurrentPage(page);
        window.scrollTo(0, 0);
      }
    };
    window.addEventListener('navigate', handleCustomNavigate);
    return () => window.removeEventListener('navigate', handleCustomNavigate);
  }, []);

  const navigateTo = useCallback((page, path) => {
    const pathMap = {
      'home': '/',
      'pricing': '/pricing',
      'audit': '/audit',
      'quick-audit': '/quick-audit',
      'quick-tools': '/quick-tools',
      'lab-insights': '/lab-insights',
      'lab-test': '/lab-test',
      'history-monitoring': '/history-monitoring',
      'competitor-comparison': '/competitor-comparison',
      'domain-comparison': '/domain-comparison',
      'methodology': '/methodology',
      'crux-alternativa': '/web-core-vitals-google-report-crux',
      'admin': '/admin',
      'global-insights': '/global-insights',
      'terms': '/terms',
      'privacy': '/privacy',
      'dpa': '/dpa',
      'credits': '/credits',
      'rankings': '/rankings',
      'top-performers': '/top-performers',
      'czech-webs-ranking': '/czech-webs-ranking',
      'monitoring': '/monitoring',
      'health-check': '/health-check',
      'checkout': '/checkout',
      'checkout-success': '/checkout/success',
      'checkout-cancel': '/checkout/cancel',
      'platform-status': '/platform-status',
      'ai-crawler-guide': '/ai-crawler-guide',
      'blog': '/blog',
      'market-insights': '/market-insights',
      'eshop-health-check': '/eshop-health-check'
    };
    const targetPath = path || pathMap[page] || '/';
    window.history.pushState({}, '', targetPath);
    setCurrentPage(page);
    window.scrollTo(0, 0);
  }, []);

  return {
    currentPage,
    setCurrentPage,
    maintenanceMode,
    maintenanceInfo,
    maintenanceChecked,
    navigateTo
  };
}

export function useViewMode() {
  const [viewMode, setViewModeState] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search);
    // Default to executive view, switch to detailed only if explicitly requested
    return urlParams.get('view') === 'detailed' ? 'detailed' : 'executive';
  });
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    const url = new URL(window.location.href);
    if (viewMode === 'detailed') {
      url.searchParams.set('view', 'detailed');
    } else {
      url.searchParams.delete('view');
    }
    window.history.replaceState({}, '', url.toString());
  }, [viewMode]);

  const setViewMode = useCallback((newMode) => {
    startTransition(() => {
      setViewModeState(newMode);
    });
  }, []);

  return { viewMode, setViewMode, isPending };
}

export function useSessionId() {
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem('sessionId');
    if (!sid) {
      sid = 'anon-' + Math.random().toString(36).substring(2);
      localStorage.setItem('sessionId', sid);
    }
    return sid;
  });
  
  return sessionId;
}

export default useUIState;
